// Live XR / HUD settings (user 2026-10-10: "start the settings dialog with an XR subpage" —
// so the panel's width, height, dead zone and text size can be tried IN the headset).
// Precedence: defaults → localStorage (this browser's saved values) → URL parameters
// (?hudwidth=… ?huddrop=… ?hudzone=… ?xrlayout=… — a one-off trial without saving).
// Consumers read through xrSettings.get() every frame (cheap) or subscribe with onChange().
// The table itself lives in hybrid/xr-settings-spec.js (the /settings page provider needs it on the server).
import { XR_SETTING_SPEC, autoLayoutWidth, settingRows } from '../hybrid/xr-settings-spec.js';
export { XR_SETTING_SPEC };
const KEY = 'xrSettings';

const listeners = new Set();
let values = load();

function load() {
	const v = {};
	for (const [k, s] of Object.entries(XR_SETTING_SPEC)) v[k] = s.dflt;
	try { Object.assign(v, JSON.parse(localStorage.getItem(KEY) || '{}')); } catch (e) { /* no storage */ }
	const q = new URLSearchParams(location.search);
	for (const [k, s] of Object.entries(XR_SETTING_SPEC)) {
		if (!s.param) continue;
		const raw = q.get(s.param);
		if (raw !== null && Number.isFinite(Number(raw))) v[k] = Number(raw);
	}
	return v;
}

export const xrSettings = {
	get(key) { return values[key]; },
	all() { return { ...values }; },
	set(key, value) {
		const spec = XR_SETTING_SPEC[key];
		if (!spec) return;
		value = Math.round(Math.min(spec.max, Math.max(spec.min, value)) * 1000) / 1000;
		if (values[key] === value) return;
		values[key] = value;
		try { localStorage.setItem(KEY, JSON.stringify(values)); } catch (e) { /* no storage */ }
		for (const fn of listeners) fn(key, value);
	},
	// the value the HUD actually uses (resolves the 0 = auto sentinel)
	effective(key) {
		if (key === 'layout') return this.layoutWidth();
		if (key === 'height') return values.height > 0 ? values.height : Math.round(values.width * window.innerHeight / window.innerWidth * 100) / 100;
		return values[key];
	},
	step(key, direction) {
		const spec = XR_SETTING_SPEC[key];
		const next = this.effective(key) + (spec.invert ? -direction : direction) * spec.step;
		this.set(key, spec.floor ? Math.max(spec.floor, next) : next);
	},
	reset() { for (const [k, s] of Object.entries(XR_SETTING_SPEC)) this.set(k, s.dflt); },
	onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
	// rows for the /settings page (views/settings.js) with the live effective values
	rows() { return settingRows((key) => this.effective(key)); },
	// derived values the HUD code uses
	deadZoneRad() { const z = values.zone; return z > 0 ? z * Math.PI / 180 : Math.atan(values.width / 2 / values.distance); },
	layoutWidth() { return values.layout > 0 ? Math.round(values.layout) : autoLayoutWidth(values.width); },
	// layout height for the panel: the window's shape by default, or the HUD HEIGHT setting (m) relative to the width
	layoutHeight(windowW, windowH) {
		const lw = this.layoutWidth();
		return Math.round(values.height > 0 ? lw * values.height / values.width : lw * windowH / windowW);
	},
};
